import { DropzoneFile } from "@/components";
import { FloatingLoader } from "@/components/other";
import useMessageMutation from "@/hooks/useMessageMutation";
import useProfile from "@/hooks/useProfile";
import { cn } from "@/libraries/cn";
import { ACCEPT_DOC_TYPE_MESSAGE, QUERY_KEYS } from "@/libraries/constant";
import { fromUnixToDateString } from "@/libraries/date";
import { getMessageTagLabel, randUuid } from "@/libraries/helper";
import { getMessagesQueryOptions } from "@/queries";
import { getHemiMessageResponseOptions } from "@/queries/options/hemi";
import { infiniteDataReplace } from "@/queries/utils/mutation";
import {
	CASE_TABS_VALUE,
	MESSAGE_TAG,
	type PostCreateMessageBodySchema,
} from "@/schemas";
import type { GetMessageDiscussionsResponse, MessageDiscussion } from "@/types";
import {
	type InfiniteData,
	useQueries,
	useQueryClient,
	useSuspenseInfiniteQuery,
} from "@tanstack/react-query";
import {
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import isEqual from "fast-deep-equal";
import cloneDeep from "lodash/cloneDeep";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Accept, DropEvent, FileRejection } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import type { PhotoProps } from "react-photo-gallery";
import { toast } from "sonner";
import { useLongPress } from "use-long-press";
import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Video from "yet-another-react-lightbox/plugins/video";
import { useMessageContext } from "./context";
import MessageItem from "./item";
import ContextMenu from "./item/ContextMenu";

interface MessageListProps {
	memberIds: number[];
	focusId?: number;
	renderEmpty: React.ReactNode;
}

function MessageList(props: MessageListProps) {
	const { focusId, memberIds = [], renderEmpty } = props;
	const channelId = useParams({
		strict: false,
		// @ts-expect-error
		select: (s) => s.id,
	});
	const fileIdx = useSearch({
		strict: false,
		// @ts-expect-error
		select: (s) => s["file-idx"],
	});
	const search = useSearch({
		strict: false,
		// @ts-expect-error
		select: (s) => s,
	});
	const queryClient = useQueryClient();
	const navigation = useNavigate();
	const lightBoxes = useRouterState({
		// @ts-expect-error
		select: (s) => (s.location.state.lightBoxes || []) as Array<PhotoProps>,
	});
	const { profile } = useProfile();
	const [cursorPosition, setCursorPosition] = useState<Record<
		"x" | "y",
		number
	> | null>(null);
	const hightlightedMessageIdxRef = useRef<number | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const { readMessage, create } = useMessageMutation();
	const { watch, setValue, getValues, reset } =
		useFormContext<PostCreateMessageBodySchema>();
	const tags = watch("tags");
	const isReadonly = useMessageContext((s) => s.isReadonly);
	const channelModule = useMessageContext((s) => s.module);
	const channelName = useMessageContext((s) => s.channelName);
	const setMessage = useMessageContext((s) => s.setMessage);
	const disabledDropzone = useMemo(() => {
		if (isReadonly) return true;
		const whitelistTags = [
			MESSAGE_TAG.FILES,
			MESSAGE_TAG.MEDIA,
			MESSAGE_TAG.PUBLISH,
		] as const;
		if (!tags) return false;
		return !whitelistTags.some((tag) => tag === tags);
	}, [tags, isReadonly]);
	const acceptFile = useMemo<Accept>(() => {
		const mediaAccepted = {
			"image/*": [],
			"video/*": [],
		} as const;
		const defaultAccepted: Accept = {
			...mediaAccepted,
			...ACCEPT_DOC_TYPE_MESSAGE.reduce((types, t) => {
				types[t] = [];
				return types;
			}, {} as Accept),
		};
		if (tags === "publish")
			return omit(defaultAccepted, Object.keys(mediaAccepted));
		return defaultAccepted;
	}, [tags]);

	const onSubmitMessage = useCallback(
		(payload: PostCreateMessageBodySchema) => {
			create.mutate(payload);
			return reset();
		},
		[create, reset],
	);

	const onDropFile = useCallback(
		(acceptedFiles: File[], fileRejections: FileRejection[], e: DropEvent) => {
			e.preventDefault();
			if (fileRejections.length) {
				const fileTypes = acceptedFiles.map((file) => file.type);
				return toast.error(`File type not allowed: ${fileTypes.join(", ")}`);
			}
			const ifDocType = acceptedFiles.every((file) =>
				ACCEPT_DOC_TYPE_MESSAGE.includes(file.type as any),
			);
			if (!tags) {
				setValue("tags", channelModule === "chats" ? "media" : "files");
			}
			setValue("files", acceptedFiles);
			if (ifDocType && channelModule === "chats") {
				const currentTags = getValues("tags");
				if (currentTags === "publish") {
					const fileLink = URL.createObjectURL(acceptedFiles[0]);
					return setValue("preview.link", fileLink);
				}
				return onSubmitMessage(getValues());
			}
		},
		[setValue, tags, channelModule, onSubmitMessage, getValues],
	);

	const depsTags = useMemo(() => {
		if (!search?.tab || search?.tab === CASE_TABS_VALUE.ALL) return undefined;
		if (search?.tab === MESSAGE_TAG.FILES) {
			return !search?.subtab || search?.subtab === "all"
				? MESSAGE_TAG.FILES
				: search?.subtab;
		}
		return search?.tab;
	}, [search?.tab, search?.subtab]);

	const queryMessages = useSuspenseInfiniteQuery(
		getMessagesQueryOptions({
			channel_name: String(channelName),
			tags: depsTags,
		}),
	);
	const resMessages = useMemo(
		() => queryMessages.data?.pages?.flatMap((page) => page.messages) ?? [],
		[queryMessages.data?.pages],
	);
	const isLoading = queryMessages.isLoading || queryMessages.isFetching;

	const messages = useMemo<MessageDiscussion[]>(() => {
		if (!resMessages?.length) return [];
		let clonedItems = cloneDeep(resMessages);
		switch (search?.tab) {
			case MESSAGE_TAG.AI:
				clonedItems = clonedItems.map((item, idx) => ({
					...item,
					tags: !isEmpty(item.hemi_responses) ? MESSAGE_TAG.AI : null,
					...(item.hemi_responses?.length && !item.user
						? {
								user: {
									// @ts-expect-error
									...(item[idx - 1] ?? {}),
									fullname: "HEMI AI",
								},
							}
						: {}),
				}));
				break;
		}
		if (search?.search && clonedItems.length) {
			clonedItems = clonedItems.map((item) => {
				if (item.system_message || !item.text) return item;
				if (item.text.toLowerCase().includes(search.search)) {
					item.text = item.text.replace(
						new RegExp(search.search, "gi"),
						(match) => `<mark>${match}</mark>`,
					);
				}
				return item;
			});
		}
		const reqTags = MESSAGE_TAG.REQUEST.toLowerCase();
		clonedItems = clonedItems
			.filter(
				(clonedItem) =>
					!(
						clonedItem.tags === reqTags &&
						(!!clonedItem.accept_at || !!clonedItem.decline_at)
					),
			)
			.map((clonedItem) => ({
				...clonedItem,
				_isMe: clonedItem.user?.id === profile?.id,
				_flag:
					clonedItem.tags !== MESSAGE_TAG.AI
						? clonedItem.id_db === -1
							? ("pending" as const)
							: clonedItem.id_db > 0 &&
									(!clonedItem.reads?.length || // When no one read the message or
										(clonedItem.reads.length === 1 && // only the sender read the message
											clonedItem.reads[0] === profile?.id)) // and the sender is the current user
								? ("sent" as const)
								: memberIds.some((id) => !clonedItem.reads.includes(id)) // When not all members read the message
									? ("delivered" as const)
									: memberIds.every((id) => clonedItem.reads.includes(id)) // When all members read the message
										? ("read" as const)
										: ("pending" as const)
						: "sent",
			}));
		const orderedMessages = orderBy(
			clonedItems,
			search?.tab === MESSAGE_TAG.AI ? ["created_at", "id_db"] : "created_at",
			search?.tab === MESSAGE_TAG.AI ? ["desc", "desc"] : "desc",
		);
		const groupedMessages = groupBy(orderedMessages, (m) =>
			fromUnixToDateString(m.created_at),
		);
		const flatMessages = flatMap(
			groupedMessages,
			(messages, date) =>
				[
					...messages,
					{
						id: randUuid(10),
						text: date,
						system_message: true,
						tags: "date", // Note: You may want to explicitly type this to avoid `@ts-expect-error`
						channel_name: channelName ?? "",
						id_db: 0,
						created_at: messages[0].created_at,
						updated_at: messages[0].updated_at,
						document_count: 0,
						document_size: "0.0 Kb",
						sent: false,
						delivered: false,
						strike: false,
						is_deleted: false,
						reply_to: null,
						invitation_status: null,
						latest_reply_date: null,
						reply_count: 0,
						reads: [],
						mention_name: [],
						user: null,
						parent: null,
						replies: [],
						documents: [],
						mentions: [],
						preview: null,
					},
				] as MessageDiscussion[],
		);
		return uniqBy(flatMessages, "id");
	}, [
		resMessages,
		channelName,
		memberIds,
		profile?.id,
		search?.tab,
		search?.search,
	]);
	const messageCount = messages.length;

	const messageVirtualizer = useVirtualizer({
		enabled: messageCount > 0,
		count: messageCount,
		overscan: 5,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
		estimateSize: () => 50,
	});
	const messageVirtualizerItems = messageVirtualizer.getVirtualItems();

	const prevUnreadIdsRef = useRef<string[]>([]);
	const unreadMessageIds = useMemo(
		() => [
			...new Set(
				resMessages
					.filter(
						(m) =>
							!m.system_message &&
							!m.hemi_responses?.length &&
							m.user?.id !== profile?.id &&
							// @ts-expect-error
							!m.reads.includes(profile?.id),
					)
					.map((m) => m.id),
			),
		],
		[resMessages, profile],
	);

	const onOpenContextMenu = (
		e: React.MouseEvent<HTMLDivElement>,
		message: MessageDiscussion,
	) => {
		if (message.is_deleted) return;
		e.preventDefault();
		e.stopPropagation();
		setMessage(message);
		return setCursorPosition({ x: e.clientX, y: e.clientY });
	};

	const onCloseContextMenu = () => {
		setCursorPosition(null);
		return setMessage(null);
	};

	const onLongPressMessage = useLongPress<HTMLDivElement, MessageDiscussion>(
		(e, meta) =>
			meta.context &&
			onOpenContextMenu(e as React.MouseEvent<HTMLDivElement>, meta.context),
	);

	const onScrollToIndex = useCallback(
		(index: number) => {
			messageVirtualizer.scrollToIndex(index, {
				align: "auto",
				behavior: "smooth",
			});
			hightlightedMessageIdxRef.current = index;
			const messageIdxTimeout = setTimeout(() => {
				hightlightedMessageIdxRef.current = null;
				if (focusId) {
					navigation({
						replace: true,
						search: {
							// @ts-expect-error
							mid: undefined,
						},
					});
				}
			}, 1000);
			return () => clearTimeout(messageIdxTimeout);
		},
		[messageVirtualizer, focusId, navigation],
	);

	const beforeScrollToIndex = useCallback(
		(index: number) => {
			const targetPage = Math.floor(index / 25);
			const currentLoadedPages = queryMessages.data
				? queryMessages.data.pages.length
				: 0;
			if (targetPage >= currentLoadedPages) {
				const loadNextPage = async () => {
					await queryMessages.fetchNextPage();
					if (
						queryMessages.data.pages &&
						targetPage >= queryMessages.data.pages.length
					) {
						loadNextPage();
					} else {
						onScrollToIndex(index);
					}
				};
				loadNextPage();
			} else {
				onScrollToIndex(index);
			}
		},
		[queryMessages.data, queryMessages.fetchNextPage, onScrollToIndex],
	);

	const onClickReply = useCallback(
		(
			e: React.MouseEvent<HTMLDivElement>,
			parentId: string | null | undefined,
		) => {
			if (!parentId) return;
			const messageIdx = messages.findIndex((m) => m.id === parentId);
			if (!messageIdx) return;
			e.stopPropagation();
			e.preventDefault();
			return beforeScrollToIndex(messageIdx);
		},
		[messages, beforeScrollToIndex],
	);

	const onClickMessage = useCallback(
		(e: React.MouseEvent<HTMLDivElement>, message: MessageDiscussion) => {
			const tags = getMessageTagLabel(message, channelModule);
			if (!tags || message.is_deleted || message.id_db <= 0) return;
			if (channelModule === "chats" && message.tags !== MESSAGE_TAG.AI) {
				e.preventDefault();
				return navigation({
					to: "/dashboard/chat/message/$id",
					params: {
						id: message.id_db,
					},
					search: {
						cid: channelId,
						sender: message.user?.fullname || "Unknown",
					},
				});
			}
			const isExternalCase = [
				"specialists advice",
				"handover",
				"referral",
			].includes(message.tags as any);
			if (channelModule === "cases" && isExternalCase) {
				e.preventDefault();
				return navigation({
					to: "/dashboard/case/message/$id",
					params: {
						id: message.id_db,
					},
					search: {
						cid: channelId,
						sender: message.user?.fullname || "Unknown",
					},
				});
			}
		},
		[channelModule, navigation, channelId],
	);

	const enableFetchMessage = useCallback(
		(message: MessageDiscussion) => {
			if (message.is_deleted || !message.id_db) return false;
			if (channelModule === "chats" && message.tags !== MESSAGE_TAG.AI) {
				return !!message.id_db && !isEmpty(message.tags);
			}
			if (channelModule === "cases") {
				return (
					!isEmpty(message.tags) &&
					["specialists advice", "handover", "referral"].includes(
						message.tags as any,
					)
				);
			}
			return false;
		},
		[channelModule],
	);

	const pendingIds = useMemo(
		() =>
			resMessages
				.filter((f) => f.hemi_responses?.[0]?.status === "pending")
				.map((m) => m.id_db),
		[resMessages],
	);

	const pendingQueries = useQueries({
		queries: pendingIds.length
			? pendingIds.map((idDb) =>
					getHemiMessageResponseOptions(idDb.toString(), {
						refetchInterval: 3000, // 3 seconds
						refetchIntervalInBackground: true,
					}),
				)
			: [],
	});
	const dataPendingQueries = useMemo(
		() => pendingQueries.filter((query) => query.data?.message),
		[pendingQueries],
	);

	useEffect(() => {
		if (dataPendingQueries.length) {
			for (const dataPendingQuery of dataPendingQueries) {
				const pendingMessageStatus =
					dataPendingQuery.data?.message?.hemi_responses?.[0]?.status;
				if (pendingMessageStatus === "complete") {
					queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
						[
							QUERY_KEYS.GET_MESSAGES,
							{ channel_name: channelName, tags: MESSAGE_TAG.AI },
						],
						(oldData) =>
							!oldData || !dataPendingQuery.data?.message
								? oldData
								: infiniteDataReplace(
										oldData,
										"messages",
										"id",
										dataPendingQuery.data.message.id,
										dataPendingQuery.data.message,
									),
					);
				}
			}
		}
	}, [dataPendingQueries, queryClient, channelName]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const handleScroll = (e: WheelEvent) => {
			e.preventDefault();
			const currentTarget = e.currentTarget as HTMLElement;

			if (currentTarget) {
				currentTarget.scrollTop -= e.deltaY;
			}
		};
		scrollRef.current?.addEventListener("wheel", handleScroll, {
			passive: false,
		});
		return () => {
			scrollRef.current?.removeEventListener("wheel", handleScroll);
		};
	}, [queryMessages.status]);

	useEffect(() => {
		const [lastItem] = [...messageVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= messages.length - 1 &&
			queryMessages.hasNextPage &&
			!queryMessages.isFetchingNextPage
		) {
			queryMessages.fetchNextPage();
		}
	}, [
		messages.length,
		queryMessages.hasNextPage,
		queryMessages.fetchNextPage,
		queryMessages.isFetchingNextPage,
		messageVirtualizerItems,
	]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const isSameAsPrev =
			prevUnreadIdsRef.current.length === unreadMessageIds.length &&
			prevUnreadIdsRef.current.every((id, idx) => id === unreadMessageIds[idx]);

		if (!isSameAsPrev && unreadMessageIds.length > 0 && channelName) {
			readMessage.mutate({
				channel_name: channelName,
				message_ids: unreadMessageIds,
			});

			prevUnreadIdsRef.current = unreadMessageIds;
		}
	}, [unreadMessageIds, channelName]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (focusId && messages.length) {
			const messageIdx = messages.findIndex((m) => m.id_db === focusId);
			if (!messageIdx) return;
			beforeScrollToIndex(messageIdx);
		}
	}, [focusId, messages]);

	if (!profile) return null;

	return (
		<DropzoneFile
			className="w-full h-full border-none rounded-md relative"
			dropzoneOptions={{
				noClick: true,
				disabled: disabledDropzone,
				accept: acceptFile,
				onDrop: onDropFile,
			}}
		>
			<FloatingLoader open={isLoading || queryMessages.isFetchingNextPage} />
			<div
				ref={scrollRef}
				className="relative h-full w-full overflow-auto"
				style={{
					transform: messageCount ? "scaleY(-1)" : undefined,
				}}
			>
				{messageCount && channelModule ? (
					<div
						className="relative w-full"
						style={{
							height: messageVirtualizer.getTotalSize(),
						}}
					>
						{messageVirtualizerItems.map((item) => {
							const message = messages[item.index];
							if (!message) return null;
							return (
								<div
									key={item.key}
									ref={messageVirtualizer.measureElement}
									data-index={item.index}
									className={cn("absolute top-0 left-0 w-full", {
										"animate-flashHighlightMsg":
											hightlightedMessageIdxRef.current === item.index,
									})}
									style={{
										transform: `translateY(${item.start}px) scaleY(-1)`,
									}}
								>
									<MessageItem
										chatModule={channelModule}
										message={message}
										profileId={profile.id}
										className="container py-2"
										enableFetchMessage={enableFetchMessage(message)}
										onContextMenu={(e) => onOpenContextMenu(e, message)}
										onClickReply={(e) => onClickReply(e, message.parent?.id)}
										onClick={(e) => onClickMessage(e, message)}
										{...onLongPressMessage(message)}
									/>
								</div>
							);
						})}
					</div>
				) : (
					renderEmpty
				)}
				<ContextMenu
					profileId={profile.id}
					position={cursorPosition}
					onClose={onCloseContextMenu}
				/>
				{lightBoxes.length ? (
					<Lightbox
						open={true}
						index={fileIdx}
						plugins={[Counter, Download, Thumbnails, Video]}
						slides={lightBoxes as any}
						close={() => {
							return navigation({
								state: {
									// @ts-expect-error
									lightBoxes: undefined,
								},
								replace: true,
							});
						}}
					/>
				) : null}
			</div>
		</DropzoneFile>
	);
}

export default memo(MessageList, (prevProps, nextProps) => {
	return isEqual(
		omit(prevProps, ["renderEmpty"]),
		omit(nextProps, ["renderEmpty"]),
	);
});
